import React from "react";
import classNames from "classnames";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
} from "reactstrap";
import SidebarAppearanceTableColorsPanel from "./SidebarAppearanceTableColorsPanel";
import SubHeader from "components/Navbars/SubHeader";
import { useTranslation } from "react-i18next";
import { HiSun, HiMoon } from "react-icons/hi2";
import {
  getShellThemeFromStorage,
  setShellThemeInStorage,
  SHELL_THEME_CHANGED_EVENT,
  THEME2_ACCENT_PRESET_FIRST,
  applyTheme2SidebarBackgroundToDocument,
  setTheme2SidebarBackgroundInStorage,
  getTheme2SidebarNavFontSizePxFromStorage,
  getTheme2SidebarNavLineHeightFromStorage,
  setTheme2SidebarNavTypographyInStorage,
  THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT,
  THEME2_SIDEBAR_NAV_FONT_SIZE_SMALL_PX,
  THEME2_SIDEBAR_NAV_FONT_SIZE_MEDIUM_PX,
  THEME2_SIDEBAR_NAV_FONT_SIZE_LARGE_PX,
  THEME2_SIDEBAR_NAV_FONT_SIZE_XLARGE_PX,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_SMALL,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_MEDIUM,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_LARGE,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_XLARGE,
  THEME2_SIDEBAR_NAV_FONT_SIZE_DEFAULT_PX,
  THEME2_SIDEBAR_NAV_LINE_HEIGHT_DEFAULT,
  snapTheme2SidebarNavFontSizePxToNearestPreset,
  snapTheme2SidebarNavLineHeightToNearestPreset,
} from "utils/sidebarShellTheme";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  applyReactTableThemeToDocument,
  getActiveThemeAccentHex,
  resetReactTableThemeInStorage,
  syncReactTableHeaderBgWithAccent,
} from "utils/reactTableTheme";

/** Bootstrap md: <768px dar ekran */
const NARROW_LAYOUT_MEDIA_QUERY = "(max-width: 767.98px)";

function useMediaLayout(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [query]);

  return matches;
}

const THEME1_PREVIEW_SRC = `${process.env.PUBLIC_URL}/theme1.png`;
const THEME2_PREVIEW_SRC = `${process.env.PUBLIC_URL}/theme2.png`;
/** Unified preview frame (theme1.png 1894×874, theme2.png 1905×858) */
const THEME_SHELL_PREVIEW_ASPECT_RATIO = 1894 / 874;

function ThemeShellPreviewImage({ shellVariant, alt }) {
  const src = shellVariant === "2" ? THEME2_PREVIEW_SRC : THEME1_PREVIEW_SRC;
  return (
    <div className="sidebar-appearance-theme-shell-img-wrap">
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

/** Aktif shell tema seçici kartı — Tema 2 info / marka mavisi */
const ACTIVE_THEME_SHELL_ACCENT = "#0092E0";

const CUSTOM_ACCENT_PRESETS_THEME1 = [
  "#0092E0",
  "#4A69BD",
  "#008060",
  "#2F3640",
  "#6D214F",
  "#57606F",
];

function getCustomAccentPresets(shellTheme) {
  if (shellTheme === "2") {
    return [THEME2_ACCENT_PRESET_FIRST, ...CUSTOM_ACCENT_PRESETS_THEME1.slice(1)];
  }
  return CUSTOM_ACCENT_PRESETS_THEME1;
}

const normalizeHexKey = (hex) => {
  if (!hex || typeof hex !== "string") return "";
  return hex.replace(/^#/, "").toUpperCase();
};

const hslToHex = (h, s, l) => {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const huePrime = h / 60;
  const secondary = chroma * (1 - Math.abs((huePrime % 2) - 1));
  const match = lightness - chroma / 2;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = secondary;
  } else if (huePrime >= 1 && huePrime < 2) {
    red = secondary;
    green = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    green = chroma;
    blue = secondary;
  } else if (huePrime >= 3 && huePrime < 4) {
    green = secondary;
    blue = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    red = secondary;
    blue = chroma;
  } else {
    red = chroma;
    blue = secondary;
  }

  const toHex = (channel) => Math.round((channel + match) * 255).toString(16).padStart(2, "0");
  return `#${toHex(red)}${toHex(green)}${toHex(blue)}`.toUpperCase();
};

const hexToHsl = (hex) => {
  const normalized = hex.replace("#", "");
  const red = parseInt(normalized.slice(0, 2), 16) / 255;
  const green = parseInt(normalized.slice(2, 4), 16) / 255;
  const blue = parseInt(normalized.slice(4, 6), 16) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;
  const lightness = (max + min) / 2;

  if (delta !== 0) {
    if (max === red) {
      hue = ((green - blue) / delta) % 6;
    } else if (max === green) {
      hue = (blue - red) / delta + 2;
    } else {
      hue = (red - green) / delta + 4;
    }
    hue *= 60;
    if (hue < 0) hue += 360;
  }

  return {
    hue: Math.round(hue),
    lightness: Math.round(lightness * 100)
  };
};

const SidebarAppearance = ({
  activeColor,
  handleActiveClick,
  account,
  isLightTheme,
  sidebarRoutes,
  sidebarMini,
  sidebarMiniLayoutReady = true,
  shellThemeVariant = "1",
}) => {
  const { t } = useTranslation();
  const isNarrowLayout = useMediaLayout(NARROW_LAYOUT_MEDIA_QUERY);
  const isTheme2 = shellThemeVariant === "2";
  const shellVariant = isTheme2 ? "2" : "1";
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== "undefined" && window.innerWidth < 992
  );

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const contentClassName = classNames("content", "sidebar-appearance-page");

  const [darkMode, setDarkMode] = React.useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });
  const initialCustomColor =
    localStorage.getItem("sidebarCustomColor") || getCustomAccentPresets(getShellThemeFromStorage())[0];
  const initialCustomHsl = hexToHsl(initialCustomColor);
  const [customColorPicker, setCustomColorPicker] = React.useState(() => initialCustomColor);
  const [customHue, setCustomHue] = React.useState(initialCustomHsl.hue);
  const [customLightness, setCustomLightness] = React.useState(Math.min(85, Math.max(25, initialCustomHsl.lightness)));

  const handleActiveMode = () => {
    setDarkMode((prev) => {
      const newValue = !prev;
      localStorage.setItem("darkMode", JSON.stringify(newValue));
      if (newValue) {
        document.body.classList.remove("white-content");
      } else {
        document.body.classList.add("white-content");
      }
      applyReactTableThemeToDocument();
      return newValue;
    });
  };

  const applyCustomColor = (hexColor) => {
    const previousAccent = getActiveThemeAccentHex();
    localStorage.setItem("sidebarCustomColor", hexColor);
    setCustomColorPicker(hexColor);
    handleActiveClick("custom");
    if (getShellThemeFromStorage() === "2") {
      setTheme2SidebarBackgroundInStorage(hexColor);
      syncReactTableHeaderBgWithAccent(previousAccent, hexColor, shellVariant);
      applyReactTableThemeToDocument();
    }
  };

  const applyPresetAccent = React.useCallback(
    (hex) => {
      const previousAccent = getActiveThemeAccentHex();
      const normalized = `#${normalizeHexKey(hex)}`;
      localStorage.setItem("sidebarCustomColor", normalized);
      setCustomColorPicker(normalized);
      handleActiveClick("custom");
      const hsl = hexToHsl(normalized);
      setCustomHue(hsl.hue);
      setCustomLightness(Math.min(85, Math.max(25, hsl.lightness)));
      if (getShellThemeFromStorage() === "2") {
        setTheme2SidebarBackgroundInStorage(normalized);
        syncReactTableHeaderBgWithAccent(previousAccent, normalized, shellVariant);
        applyReactTableThemeToDocument();
      }
    },
    [handleActiveClick, shellVariant]
  );

  const handleHueChange = (event) => {
    const nextHue = Number(event.target.value);
    setCustomHue(nextHue);
    applyCustomColor(hslToHex(nextHue, 100, customLightness));
  };

  const handleLightnessChange = (event) => {
    const nextLightness = Number(event.target.value);
    setCustomLightness(nextLightness);
    applyCustomColor(hslToHex(customHue, 100, nextLightness));
  };

  React.useEffect(() => {
    if (darkMode) {
      document.body.classList.remove("white-content");
    } else {
      document.body.classList.add("white-content");
    }
  }, [darkMode]);

  const [activeShellTheme, setActiveShellTheme] = React.useState(() =>
    getShellThemeFromStorage()
  );

  React.useEffect(() => {
    const sync = () => setActiveShellTheme(getShellThemeFromStorage());
    sync();
    window.addEventListener(SHELL_THEME_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SHELL_THEME_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const [theme2NavFontSizePx, setTheme2NavFontSizePx] = React.useState(() =>
    typeof window === "undefined" ? THEME2_SIDEBAR_NAV_FONT_SIZE_DEFAULT_PX : getTheme2SidebarNavFontSizePxFromStorage()
  );
  const [theme2NavLineHeight, setTheme2NavLineHeight] = React.useState(() =>
    typeof window === "undefined" ? THEME2_SIDEBAR_NAV_LINE_HEIGHT_DEFAULT : getTheme2SidebarNavLineHeightFromStorage()
  );
  const [theme2TypoHoverKey, setTheme2TypoHoverKey] = React.useState(null);

  React.useEffect(() => {
    setTheme2TypoHoverKey(null);
  }, [activeShellTheme]);

  React.useEffect(() => {
    const syncTypoState = () => {
      setTheme2NavFontSizePx(getTheme2SidebarNavFontSizePxFromStorage());
      setTheme2NavLineHeight(getTheme2SidebarNavLineHeightFromStorage());
    };
    window.addEventListener(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT, syncTypoState);
    window.addEventListener("storage", syncTypoState);
    return () => {
      window.removeEventListener(THEME2_SIDEBAR_NAV_TYPOGRAPHY_CHANGED_EVENT, syncTypoState);
      window.removeEventListener("storage", syncTypoState);
    };
  }, []);

  const theme2TypographyEnabled = activeShellTheme === "2";

  const theme2NavFontPresetSelectedPx = React.useMemo(
    () => snapTheme2SidebarNavFontSizePxToNearestPreset(theme2NavFontSizePx),
    [theme2NavFontSizePx]
  );

  const theme2NavLineHeightPresetSelected = React.useMemo(
    () => snapTheme2SidebarNavLineHeightToNearestPreset(theme2NavLineHeight),
    [theme2NavLineHeight]
  );

  const activeColorStyle = React.useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const [themeModeTab, setThemeModeTab] = React.useState("appearance");

  const resetAccentToDefaultBlue = React.useCallback(() => {
    const defaultAccent = ACTIVE_THEME_SHELL_ACCENT;
    localStorage.setItem("sidebarCustomColor", defaultAccent);
    setCustomColorPicker(defaultAccent);
    const hsl = hexToHsl(defaultAccent);
    setCustomHue(hsl.hue);
    setCustomLightness(Math.min(85, Math.max(25, hsl.lightness)));
    handleActiveClick("custom");
    // SubHeader accent only — keep Theme 2 sidebar background as-is.
    applyTheme2SidebarBackgroundToDocument();
  }, [handleActiveClick]);

  const handleTableColorsReset = React.useCallback(() => {
    resetReactTableThemeInStorage(null, shellVariant);
    if (shellVariant === "2") {
      resetAccentToDefaultBlue();
    }
  }, [resetAccentToDefaultBlue, shellVariant]);

  const themeModeTabsNav = (
    <Nav tabs className="border-0 flex-nowrap" style={{ borderBottom: "none" }}>
      <NavItem>
        <NavLink
          href="#"
          className={classNames(
            "sidebar-appearance-theme-mode-tab",
            themeModeTab === "appearance" ? "active font-weight-bold" : "text-muted"
          )}
          style={{
            background: "none",
            border: "none",
            fontSize: 14,
            position: "relative",
            cursor: "pointer",
            color:
              themeModeTab === "appearance"
                ? activeColorStyle.borderColor
                : undefined,
            fontWeight: themeModeTab === "appearance" ? 700 : 500,
            marginRight: 16,
            paddingLeft: 0,
            paddingRight: 0,
          }}
          onClick={(e) => {
            e.preventDefault();
            setThemeModeTab("appearance");
          }}
        >
          {t("sidebarAppearance.themeModeTabs.appearance")}
          {themeModeTab === "appearance" && (
            <div
              style={{
                height: 3,
                background: activeColorStyle.borderColor,
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -8,
              }}
            />
          )}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink
          href="#"
          className={classNames(
            "sidebar-appearance-theme-mode-tab",
            themeModeTab === "tableColors" ? "active font-weight-bold" : "text-muted"
          )}
          style={{
            background: "none",
            border: "none",
            fontSize: 14,
            position: "relative",
            cursor: "pointer",
            color:
              themeModeTab === "tableColors"
                ? activeColorStyle.borderColor
                : undefined,
            fontWeight: themeModeTab === "tableColors" ? 700 : 500,
            paddingLeft: 0,
            paddingRight: 0,
          }}
          onClick={(e) => {
            e.preventDefault();
            setThemeModeTab("tableColors");
          }}
        >
          {t("sidebarAppearance.themeModeTabs.tableColors")}
          {themeModeTab === "tableColors" && (
            <div
              style={{
                height: 3,
                background: activeColorStyle.borderColor,
                position: "absolute",
                left: 0,
                right: 0,
                bottom: -8,
              }}
            />
          )}
        </NavLink>
      </NavItem>
    </Nav>
  );

  const theme2TypographyBtnLayout = React.useMemo(
    () =>
      isNarrowLayout
        ? { flex: "1 1 auto", minWidth: 0, width: "100%", margin: 0, minHeight: 44 }
        : { flex: "1 1 64px", minWidth: 64, minHeight: 36, margin: 0 },
    [isNarrowLayout]
  );

  const getTheme2TypoButtonStyle = React.useCallback(
    (accent) => {
      const idleBorderColor = isLightTheme
        ? "rgba(0, 0, 0, 0.22)"
        : "rgba(255, 255, 255, 0.35)";
      const shared = {
        ...theme2TypographyBtnLayout,
        boxSizing: "border-box",
        fontWeight: 600,
        lineHeight: 1.2,
        borderWidth: 2,
        borderStyle: "solid",
        padding: "0.25rem 0.5rem",
      };
      if (accent) {
        return {
          ...shared,
          borderColor: activeColorStyle.borderColor,
          background: activeColorStyle.background,
          backgroundImage: activeColorStyle.backgroundImage,
          color: "#fff",
        };
      }
      return {
        ...shared,
        borderColor: idleBorderColor,
        background: isLightTheme ? "#ffffff" : "rgba(255, 255, 255, 0.08)",
        backgroundImage: "none",
        color: isLightTheme ? "#3c4858" : "rgba(255, 255, 255, 0.9)",
      };
    },
    [activeColorStyle, isLightTheme, theme2TypographyBtnLayout]
  );

  const customAccentPresets = React.useMemo(
    () => getCustomAccentPresets(activeShellTheme),
    [activeShellTheme]
  );

  const getPresetSwatchBorderStyle = React.useCallback(
    (selected) => {
      if (selected) {
        return isLightTheme
          ? {
              border: "2px solid #ffffff",
              boxShadow: `0 0 0 3px ${ACTIVE_THEME_SHELL_ACCENT}`,
            }
          : {
              border: "2px solid #ffffff",
              boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.65)",
            };
      }
      return isLightTheme
        ? { border: "1px solid rgba(0, 0, 0, 0.35)" }
        : { border: "1px solid rgba(255, 255, 255, 0.45)" };
    },
    [isLightTheme]
  );

  const customAccentPresetSwatches = (
    <div
      role="group"
      className={classNames(
        "sidebar-appearance-preset-swatches",
        isLightTheme && "sidebar-appearance-preset-swatches--light"
      )}
      aria-label={t("sidebarAppearance.presetColors")}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        flexWrap: "wrap",
      }}
    >
      {customAccentPresets.map((preset) => {
        const selected = normalizeHexKey(customColorPicker) === normalizeHexKey(preset);
        return (
          <button
            key={preset}
            type="button"
            className={classNames(
              "sidebar-appearance-preset-swatch",
              selected && "sidebar-appearance-preset-swatch--selected"
            )}
            aria-pressed={selected}
            title={preset}
            onClick={() => applyPresetAccent(preset)}
            style={{
              width: 22,
              height: 22,
              padding: 0,
              backgroundColor: preset,
              cursor: "pointer",
              flexShrink: 0,
              boxSizing: "border-box",
              ...getPresetSwatchBorderStyle(selected),
            }}
          />
        );
      })}
    </div>
  );

  const themeToggleIconColor = isLightTheme ? "#3c4858" : "#FFFFFF";
  const themeToggleIconSize = 24;

  const themeControls = (
    <>
      <div
        className="d-flex flex-wrap align-items-center mb-3 sidebar-appearance-theme-controls-row"
        style={{ gap: "16px 24px" }}
      >
        <div style={{ flex: "1 1 100%", minWidth: 0, maxWidth: "100%", width: "100%" }}>
          <div className="sidebar-appearance-custom-section mb-2">
            <div
              className="d-flex align-items-center justify-content-between sidebar-appearance-custom-section-title-row"
              style={{ width: "100%", gap: 10 }}
            >
              <Label className="mb-0 sidebar-appearance-typo-label">
                {t("sidebarAppearance.customSection")}
              </Label>
              <button
                type="button"
                className="sidebar-appearance-theme-toggle-btn sidebar-appearance-custom-section-theme-btn flex-shrink-0"
                onClick={handleActiveMode}
                title={darkMode ? t("sidebarAppearance.light") : t("sidebarAppearance.dark")}
                aria-label={darkMode ? t("sidebarAppearance.light") : t("sidebarAppearance.dark")}
              >
                {darkMode ? (
                  <HiSun size={themeToggleIconSize} color={themeToggleIconColor} />
                ) : (
                  <HiMoon size={themeToggleIconSize} color={themeToggleIconColor} />
                )}
              </button>
            </div>
            {customAccentPresetSwatches}
          </div>
          <div style={{ maxWidth: "min(480px, 100%)" }}>
            <div className="sidebar-appearance-color-picker-row" style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", width: "100%" }}>
              <div
                className="sidebar-appearance-color-preview-swatch"
                style={{
                  width: "26px",
                  height: "36px",
                  background: customColorPicker,
                  border: "1px solid rgba(255, 255, 255, 0.45)",
                  flex: "0 0 auto",
                }}
              />
              <div className="sidebar-appearance-slider-stack" style={{ flex: "1 1 0", display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
                <input
                  type="range"
                  className="sidebar-appearance-slider sidebar-appearance-hue"
                  min="0"
                  max="360"
                  value={customHue}
                  onChange={handleHueChange}
                  style={{ width: "100%", margin: 0 }}
                />
                <input
                  type="range"
                  className="sidebar-appearance-slider sidebar-appearance-lightness"
                  min="25"
                  max="85"
                  value={customLightness}
                  onChange={handleLightnessChange}
                  style={{ width: "100%", margin: 0 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const previewChromeBorder = isLightTheme ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.14)";
  const previewChromeBg = isLightTheme ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.04)";

  const themeShellColStyle = (isActive) => ({
    border: `2px solid ${isActive ? ACTIVE_THEME_SHELL_ACCENT : previewChromeBorder}`,
    background: isActive ? ACTIVE_THEME_SHELL_ACCENT : previewChromeBg,
    cursor: "pointer",
    boxSizing: "border-box",
  });

  const themeShellColLabelStyle = (isActive) => ({
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: isActive ? "#ffffff" : isLightTheme ? "#3c4858" : "#ffffff",
    opacity: isActive ? 1 : isLightTheme ? 0.85 : 1,
  });

  return (
    <div
      className={contentClassName}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
      }}
    >
      <style>{`
        .sidebar-appearance-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 8px;
          outline: none;
          border: none;
          padding: 0;
        }
        .sidebar-appearance-slider::-webkit-slider-runnable-track {
          height: 8px;
          border: none;
        }
        .sidebar-appearance-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          margin-top: -3px;
          border: 2px solid #ffffff;
          background: #f4f4f4;
          cursor: pointer;
        }
        .sidebar-appearance-slider::-moz-range-track {
          height: 8px;
          border: none;
        }
        .sidebar-appearance-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border: 2px solid #ffffff;
          background: #f4f4f4;
          cursor: pointer;
        }
        .sidebar-appearance-hue::-webkit-slider-runnable-track {
          background: linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%);
        }
        .sidebar-appearance-hue::-moz-range-track {
          background: linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%);
        }
        .sidebar-appearance-lightness::-webkit-slider-runnable-track {
          background: linear-gradient(to right, #242424 0%, #f0f0f0 100%);
        }
        .sidebar-appearance-lightness::-moz-range-track {
          background: linear-gradient(to right, #242424 0%, #f0f0f0 100%);
        }
        .sidebar-appearance-page .sidebar-appearance-theme-tabs .sidebar-appearance-theme-mode-tab:not(.active),
        .sidebar-appearance-page .sidebar-appearance-theme-tabs .sidebar-appearance-theme-mode-tab:not(.active):hover,
        .sidebar-appearance-page .sidebar-appearance-theme-tabs .sidebar-appearance-theme-mode-tab:not(.active):focus {
          color: #ffffff !important;
        }
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-tabs .sidebar-appearance-theme-mode-tab:not(.active),
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-tabs .sidebar-appearance-theme-mode-tab:not(.active):hover,
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-tabs .sidebar-appearance-theme-mode-tab:not(.active):focus {
          color: #3c4858 !important;
        }
        .sidebar-appearance-page .sidebar-appearance-theme-section-heading,
        .sidebar-appearance-theme-mode .sidebar-appearance-section-label {
          font-size: 0.8rem !important;
          letter-spacing: 0.05em !important;
          text-transform: uppercase;
          color: #ffffff !important;
        }
        .sidebar-appearance-page .sidebar-appearance-theme-mode .sidebar-appearance-typo-label {
          color: #ffffff !important;
          font-size: 0.875rem !important;
          font-weight: 600 !important;
        }
        .sidebar-appearance-page .sidebar-appearance-theme-mode .sidebar-appearance-typo-label-meta {
          color: rgba(255, 255, 255, 0.8) !important;
          font-weight: 400 !important;
        }
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-section-heading,
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-mode .sidebar-appearance-section-label {
          color: #3c4858 !important;
        }
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-mode .sidebar-appearance-typo-label {
          color: #3c4858 !important;
        }
        .white-content .sidebar-appearance-page .sidebar-appearance-theme-mode .sidebar-appearance-typo-label-meta {
          color: #6c757d !important;
        }
        .sidebar-appearance-theme-toggle-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: transparent;
          padding: 6px;
          border-radius: 0;
          cursor: pointer;
          line-height: 1;
          flex-shrink: 0;
        }
        .sidebar-appearance-theme-toggle-btn:hover,
        .sidebar-appearance-theme-toggle-btn:focus {
          opacity: 0.85;
          outline: none;
        }
        .sidebar-appearance-theme-toggle-btn:focus-visible {
          outline: 2px solid var(--info, #1d8cf8);
          outline-offset: 2px;
        }
        .sidebar-appearance-custom-section-header .sidebar-appearance-custom-section-theme-btn {
          margin-left: auto;
        }
        .sidebar-appearance-preset-swatches--light .sidebar-appearance-preset-swatch--selected {
          border: 2px solid #ffffff !important;
          box-shadow: 0 0 0 3px ${ACTIVE_THEME_SHELL_ACCENT} !important;
        }
        .sidebar-appearance-preset-swatches:not(.sidebar-appearance-preset-swatches--light) .sidebar-appearance-preset-swatch--selected {
          border: 2px solid #ffffff !important;
          box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.65) !important;
        }
        .sidebar-appearance-theme-mode .label-switch {
          font-size: 0.95rem !important;
        }
        .sidebar-appearance-theme-mode .sidebar-appearance-slider {
          height: 10px;
        }
        .sidebar-appearance-theme-mode .sidebar-appearance-slider::-webkit-slider-runnable-track {
          height: 10px;
        }
        .sidebar-appearance-theme-mode .sidebar-appearance-slider::-webkit-slider-thumb {
          width: 16px;
          height: 16px;
          margin-top: -3px;
        }
        .sidebar-appearance-theme-mode .sidebar-appearance-slider::-moz-range-track {
          height: 10px;
        }
        .sidebar-appearance-theme-mode .sidebar-appearance-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
        }
        .sidebar-appearance-wp-triple {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 16px;
          width: 100%;
          align-items: stretch;
        }
        @media (max-width: 991.98px) {
          .sidebar-appearance-wp-triple {
            grid-template-columns: 1fr;
          }
        }
        .sidebar-appearance-wp-triple-col {
          display: flex;
          flex-direction: column;
          min-width: 0;
          gap: 10px;
          padding: 12px;
          min-height: 0;
          height: 100%;
          box-sizing: border-box;
          border: 1px solid rgba(128, 128, 128, 0.25);
          background: rgba(128, 128, 128, 0.06);
        }
        .sidebar-appearance-wp-preview-frame {
          width: 100%;
          aspect-ratio: ${THEME_SHELL_PREVIEW_ASPECT_RATIO};
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
        }
        .sidebar-appearance-theme-shell-img-wrap {
          width: 100%;
          height: 100%;
          line-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .sidebar-appearance-theme-shell-img-wrap img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
          object-position: center top;
          pointer-events: none;
        }
        .sidebar-appearance-wp-triple-col[role="button"]:focus-visible {
          outline: 2px solid var(--info, #1d8cf8);
          outline-offset: 2px;
        }
        @media (min-width: 992px) {
          .sidebar-appearance-theme-split-right {
            padding-left: 1.25rem;
            padding-right: 0rem;
            border-left: 1px solid rgba(128, 128, 128, 0.28);
          }
        }
        .sidebar-appearance-page {
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
          box-sizing: border-box;
        }
        .sidebar-appearance-page *,
        .sidebar-appearance-page *::before,
        .sidebar-appearance-page *::after {
          box-sizing: border-box;
        }
        .sidebar-appearance-page .sidebar-appearance-layout-row {
          margin-left: 0;
          margin-right: 0;
        }
        .sidebar-appearance-page .sidebar-appearance-layout-row > [class*="col-"] {
          min-width: 0;
        }
        .sidebar-appearance-typo-btn-grid .sidebar-appearance-typo-btn {
          transform: none !important;
          box-shadow: none !important;
        }
        .sidebar-appearance-typo-btn-grid .sidebar-appearance-typo-btn:hover,
        .sidebar-appearance-typo-btn-grid .sidebar-appearance-typo-btn:focus,
        .sidebar-appearance-typo-btn-grid .sidebar-appearance-typo-btn:active {
          transform: none !important;
          box-shadow: none !important;
        }
        .sidebar-appearance-page .card,
        .sidebar-appearance-page .card-body,
        .sidebar-appearance-theme-shell-img-wrap,
        .sidebar-appearance-theme-shell-img-wrap img {
          max-width: 100%;
        }
        @media (max-width: 991.98px) {
          .sidebar-appearance-theme-controls-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .sidebar-appearance-theme-controls .form-group label {
            word-break: break-word;
          }
          .sidebar-appearance-custom-section-title-row {
            width: 100%;
          }
          .sidebar-appearance-custom-section .sidebar-appearance-preset-swatches {
            width: 100%;
            margin-top: 10px;
          }
          .sidebar-appearance-slider-stack {
            flex: 1 1 0 !important;
            min-width: 0 !important;
            width: 100%;
          }
          .sidebar-appearance-color-picker-row {
            width: 100%;
          }
          .sidebar-appearance-theme-split-right {
            padding-left: 0 !important;
            border-left: none !important;
            margin-top: 8px;
            padding-top: 16px !important;
            border-top: 1px solid rgba(128, 128, 128, 0.28);
          }
          .sidebar-appearance-typo-btn-grid {
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 8px !important;
            width: 100%;
          }
          .sidebar-appearance-typo-btn-grid .sidebar-appearance-typo-btn {
            min-height: 44px;
            flex: none !important;
            width: 100% !important;
            min-width: 0 !important;
          }
        }
        @media (max-width: 767.98px) {
          .sidebar-appearance-page {
            padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
          }
          .sidebar-appearance-page .card-body {
            padding: 14px 12px !important;
          }
          .sidebar-appearance-preset-swatch {
            width: 36px !important;
            height: 36px !important;
          }
          .sidebar-appearance-color-preview-swatch {
            width: 32px !important;
            height: 44px !important;
          }
          .sidebar-appearance-theme-mode .sidebar-appearance-slider::-webkit-slider-thumb {
            width: 22px;
            height: 22px;
            margin-top: -7px;
          }
          .sidebar-appearance-theme-mode .sidebar-appearance-slider::-moz-range-thumb {
            width: 22px;
            height: 22px;
          }
          .sidebar-appearance-wp-triple-col {
            padding: 14px 12px;
            min-height: 48px;
          }
        }
        @media (max-width: 480px) {
          .sidebar-appearance-page .card-body {
            padding: 12px 8px !important;
          }
          .sidebar-appearance-preset-swatches {
            display: grid !important;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 8px;
          }
          .sidebar-appearance-preset-swatch {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1 / 1;
          }
          .sidebar-appearance-color-picker-row {
            flex-direction: column;
            align-items: stretch !important;
          }
          .sidebar-appearance-color-preview-swatch {
            width: 100% !important;
            max-width: 100%;
            height: 36px !important;
          }
        }
      `}</style>
      <div style={{ width: "100%" }}>
        <div className="sidebar-appearance-subheader-wrap">
          <SubHeader
            activeColor={activeColor}
            back={-1}
            title={t("sidebarAppearance.gearTitle")}
            theme2ScrollGapFill={isTheme2}
            isLightTheme={isLightTheme}
          />
        </div>
      <Row className="sidebar-appearance-layout-row">
        <Col md="12">
          <div className="w-100 sidebar-appearance-theme-tabs institution-detail-tabs">
            <div
              className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between"
              style={{ overflow: "visible" }}
            >
              <div className="flex-grow-1 min-width-0" style={{ overflow: "visible" }}>
                {themeModeTabsNav}
              </div>
              {themeModeTab === "tableColors" ? (
                <div className="d-none d-lg-flex align-items-center flex-shrink-0 ml-lg-2">
                  <Button
                    color="secondary"
                    size="sm"
                    className="sidebar-appearance-table-colors-reset-btn"
                    onClick={handleTableColorsReset}
                  >
                    {t("sidebarAppearance.tableColorsSection.reset")}
                  </Button>
                </div>
              ) : null}
            </div>
            {themeModeTab === "tableColors" ? (
              <div className="d-flex d-lg-none flex-column w-100 mt-2 mb-2">
                <Button
                  color="secondary"
                  size="sm"
                  className="w-100 sidebar-appearance-table-colors-reset-btn"
                  onClick={handleTableColorsReset}
                >
                  {t("sidebarAppearance.tableColorsSection.reset")}
                </Button>
              </div>
            ) : null}
          </div>
          <Card className="mb-2">
            <CardBody className="p-2">
              <TabContent activeTab={themeModeTab}>
                    <TabPane tabId="appearance">
                      <Row className="align-items-start sidebar-appearance-layout-row">
                        <Col xs="12" lg="6" className="mb-4 pl-0 mb-lg-0">
                          <div className="sidebar-appearance-theme-controls sidebar-appearance-theme-mode">
                            {themeControls}
                          </div>
                        </Col>
                        <Col xs="12" lg="6" className="sidebar-appearance-theme-split-right">
                  <div
                    className="sidebar-appearance-theme-controls sidebar-appearance-theme-mode"
                    style={{
                      opacity: theme2TypographyEnabled ? 1 : 0.5,
                      pointerEvents: theme2TypographyEnabled ? "auto" : "none",
                    }}
                    aria-disabled={!theme2TypographyEnabled}
                  >
                    <FormGroup className="mb-3">
                      <Label className="mb-2 sidebar-appearance-typo-label ">
                        {t("sidebarAppearance.theme2Typography.fontSizeLabel")}{" "}
                        <span className="sidebar-appearance-typo-label-meta">({theme2NavFontPresetSelectedPx}px)</span>
                      </Label>
                      <div
                        role="group"
                        className="sidebar-appearance-typo-btn-grid"
                        aria-label={t("sidebarAppearance.theme2Typography.fontSizeLabel")}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          maxWidth: "100%",
                        }}
                      >
                        {[
                          { px: THEME2_SIDEBAR_NAV_FONT_SIZE_SMALL_PX, key: "fontSizeSmall" },
                          { px: THEME2_SIDEBAR_NAV_FONT_SIZE_MEDIUM_PX, key: "fontSizeMedium" },
                          { px: THEME2_SIDEBAR_NAV_FONT_SIZE_LARGE_PX, key: "fontSizeLarge" },
                          { px: THEME2_SIDEBAR_NAV_FONT_SIZE_XLARGE_PX, key: "fontSizeXlarge" },
                        ].map(({ px, key }) => {
                          const selected = theme2NavFontPresetSelectedPx === px;
                          const hovered = theme2TypographyEnabled && theme2TypoHoverKey === key;
                          const showAccent = selected || hovered;
                          return (
                            <button
                              key={key}
                              type="button"
                              className="btn btn-sm sidebar-appearance-typo-btn"
                              aria-pressed={selected}
                              disabled={!theme2TypographyEnabled}
                              onMouseEnter={() => theme2TypographyEnabled && setTheme2TypoHoverKey(key)}
                              onMouseLeave={() => setTheme2TypoHoverKey(null)}
                              onClick={() => {
                                setTheme2NavFontSizePx(px);
                                if (theme2TypographyEnabled) {
                                  setTheme2SidebarNavTypographyInStorage(px, theme2NavLineHeight);
                                }
                              }}
                              style={{
                                ...getTheme2TypoButtonStyle(showAccent),
                                cursor: theme2TypographyEnabled ? "pointer" : "not-allowed",
                              }}
                            >
                              {t(`sidebarAppearance.theme2Typography.${key}`)}
                            </button>
                          );
                        })}
                      </div>
                    </FormGroup>
                    <FormGroup className="mb-0">
                      <Label className="mb-2 sidebar-appearance-typo-label">
                        {t("sidebarAppearance.theme2Typography.lineHeightLabel")}{" "}
                        <span className="sidebar-appearance-typo-label-meta">({theme2NavLineHeightPresetSelected})</span>
                      </Label>
                      <div
                        role="group"
                        className="sidebar-appearance-typo-btn-grid"
                        aria-label={t("sidebarAppearance.theme2Typography.lineHeightLabel")}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 8,
                          maxWidth: "100%",
                        }}
                      >
                        {[
                          { lh: THEME2_SIDEBAR_NAV_LINE_HEIGHT_SMALL, key: "lineHeightSmall" },
                          { lh: THEME2_SIDEBAR_NAV_LINE_HEIGHT_MEDIUM, key: "lineHeightMedium" },
                          { lh: THEME2_SIDEBAR_NAV_LINE_HEIGHT_LARGE, key: "lineHeightLarge" },
                          { lh: THEME2_SIDEBAR_NAV_LINE_HEIGHT_XLARGE, key: "lineHeightXlarge" },
                        ].map(({ lh, key }) => {
                          const selected = theme2NavLineHeightPresetSelected === lh;
                          const hovered = theme2TypographyEnabled && theme2TypoHoverKey === key;
                          const showAccent = selected || hovered;
                          return (
                            <button
                              key={key}
                              type="button"
                              className="btn btn-sm sidebar-appearance-typo-btn"
                              aria-pressed={selected}
                              disabled={!theme2TypographyEnabled}
                              onMouseEnter={() => theme2TypographyEnabled && setTheme2TypoHoverKey(key)}
                              onMouseLeave={() => setTheme2TypoHoverKey(null)}
                              onClick={() => {
                                setTheme2NavLineHeight(lh);
                                if (theme2TypographyEnabled) {
                                  setTheme2SidebarNavTypographyInStorage(theme2NavFontSizePx, lh);
                                }
                              }}
                              style={{
                                ...getTheme2TypoButtonStyle(showAccent),
                                cursor: theme2TypographyEnabled ? "pointer" : "not-allowed",
                              }}
                            >
                              {t(`sidebarAppearance.theme2Typography.${key}`)}
                            </button>
                          );
                        })}
                      </div>
                    </FormGroup>
                  </div>
                        </Col>
                      </Row>
                    </TabPane>
                    <TabPane tabId="tableColors">
                      <SidebarAppearanceTableColorsPanel
                        activeColor={activeColor}
                        isLightTheme={isLightTheme}
                        shellThemeVariant={activeShellTheme}
                      />
                    </TabPane>
                  </TabContent>
            </CardBody>
          </Card>

          {account ? (
            <Card>
              <CardBody className="pt-3">
                <div className="sidebar-appearance-wp-triple">
                    <div
                      className="sidebar-appearance-wp-triple-col"
                      role="button"
                      tabIndex={0}
                      aria-pressed={activeShellTheme === "1"}
                      aria-label={t("sidebarAppearance.wpLayout.applyThemeShell", {
                        name: t("sidebarAppearance.themeCarousel.slide1Label"),
                      })}
                      onClick={() => setShellThemeInStorage("1")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShellThemeInStorage("1");
                        }
                      }}
                      style={themeShellColStyle(activeShellTheme === "1")}
                    >
                      <p
                        className="mb-0 text-uppercase"
                        style={themeShellColLabelStyle(activeShellTheme === "1")}
                      >
                        {t("sidebarAppearance.themeCarousel.slide1Label")}
                      </p>
                      <div
                        className="sidebar-appearance-wp-preview-frame"
                        style={{
                          background: isLightTheme ? "#ffffff" : "#1e1e2d",
                          border: `1px solid ${previewChromeBorder}`,
                        }}
                      >
                        <ThemeShellPreviewImage
                          shellVariant="1"
                          alt={t("sidebarAppearance.themeCarousel.slide1Label")}
                        />
                      </div>
                    </div>
                    <div
                      className="sidebar-appearance-wp-triple-col"
                      role="button"
                      tabIndex={0}
                      aria-pressed={activeShellTheme === "2"}
                      aria-label={t("sidebarAppearance.wpLayout.applyThemeShell", {
                        name: t("sidebarAppearance.themeCarousel.slide2Label"),
                      })}
                      onClick={() => setShellThemeInStorage("2")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setShellThemeInStorage("2");
                        }
                      }}
                      style={themeShellColStyle(activeShellTheme === "2")}
                    >
                      <p
                        className="mb-0 text-uppercase"
                        style={themeShellColLabelStyle(activeShellTheme === "2")}
                      >
                        {t("sidebarAppearance.themeCarousel.slide2Label")}
                      </p>
                      <div
                        className="sidebar-appearance-wp-preview-frame white-content"
                        style={{
                          background: isLightTheme ? "#ffffff" : "#1e1e2d",
                          border: `1px solid ${previewChromeBorder}`,
                        }}
                      >
                        <ThemeShellPreviewImage
                          shellVariant="2"
                          alt={t("sidebarAppearance.themeCarousel.slide2Label")}
                        />
                      </div>
                    </div>
                </div>
              </CardBody>
            </Card>
          ) : null}
        </Col>
      </Row>
      </div>
    </div>
  );
};

export default SidebarAppearance;
